import { useState, useEffect } from 'react'
import { supabase } from '../supabaseClient'

const BLUE = '#185FA5'
const RED = '#A32D2D'

const DEFAULT_POSITIONS = [
  { num: 1, label: 'Goalkeeper' },
  { num: 2, label: 'Right Corner Back' },
  { num: 3, label: 'Full Back' },
  { num: 4, label: 'Left Corner Back' },
  { num: 5, label: 'Right Half Back' },
  { num: 6, label: 'Centre Back' },
  { num: 7, label: 'Left Half Back' },
  { num: 8, label: 'Midfield' },
  { num: 9, label: 'Midfield' },
  { num: 10, label: 'Right Half Forward' },
  { num: 11, label: 'Centre Forward' },
  { num: 12, label: 'Left Half Forward' },
  { num: 13, label: 'Right Corner Forward' },
  { num: 14, label: 'Full Forward' },
  { num: 15, label: 'Left Corner Forward' },
]

export default function OppositionTab({ fixture, events, isTracker }) {
  const [oppPlayers, setOppPlayers] = useState([])
  const [subTab, setSubTab] = useState('lineup')
  const [editingId, setEditingId] = useState(null)
  const [editName, setEditName] = useState('')
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(true)
  const [swapFirst, setSwapFirst] = useState(null)
  const [historyLoaded, setHistoryLoaded] = useState(false)

useEffect(() => {
    console.log('OppositionTab fixture:', fixture?.id, fixture?.opponent)
    if (fixture?.id) loadOppPlayers()
  }, [fixture?.id])

  async function loadOppPlayers() {
    setLoading(true)
    const { data } = await supabase
      .from('opposition_players')
      .select('*')
      .eq('fixture_id', fixture.id)
      .order('jersey_number')
    if (data && data.length > 0) {
      setOppPlayers(data)
    } else {
      await initDefaultLineup()
    }
    setLoading(false)
  }

async function initDefaultLineup() {
    // Check for previous fixture against same opponent
    const { data: prevPlayers } = await supabase
      .from('opposition_players')
      .select('jersey_number, full_name, position_slot')
      .eq('club_name', fixture.opponent)
      .neq('fixture_id', fixture.id)
      .not('full_name', 'is', null)
      .order('created_at', { ascending: false })
      .limit(26)

    // Build name lookup from previous fixture
    const prevNameMap = {}
    if (prevPlayers && prevPlayers.length > 0) {
      prevPlayers.forEach(p => {
        if (!prevNameMap[p.jersey_number]) {
          prevNameMap[p.jersey_number] = { full_name: p.full_name, position_slot: p.position_slot }
        }
      })
    }

    const hasPrevData = Object.keys(prevNameMap).length > 0

    const starterRows = DEFAULT_POSITIONS.map(p => ({
      fixture_id: fixture.id,
      club_name: fixture.opponent,
      jersey_number: p.num,
      position_slot: p.num,
      full_name: prevNameMap[p.num]?.full_name || null,
      is_starting: true,
      goals: 0,
      points: 0,
    }))
    const subRows = Array.from({ length: 11 }, (_, i) => ({
      fixture_id: fixture.id,
      club_name: fixture.opponent,
      jersey_number: i + 16,
      position_slot: prevNameMap[i + 16]?.position_slot || null,
      full_name: prevNameMap[i + 16]?.full_name || null,
      is_starting: false,
      goals: 0,
      points: 0,
    }))

    const { data } = await supabase
      .from('opposition_players')
      .insert([...starterRows, ...subRows])
      .select()

    setOppPlayers(data || [])
    setHistoryLoaded(hasPrevData)
  }

  async function saveName(player) {
    setSaving(true)
    await supabase.from('opposition_players')
      .update({ full_name: editName.trim() || null })
      .eq('id', player.id)
    setOppPlayers(prev => prev.map(p => p.id === player.id ? { ...p, full_name: editName.trim() || null } : p))
    setEditingId(null)
    setEditName('')
    setSaving(false)
  }

async function toggleStandout(player) {
    const newVal = !player.standout
    await supabase.from('opposition_players').update({ standout: newVal }).eq('id', player.id)
    setOppPlayers(prev => prev.map(p => p.id === player.id ? { ...p, standout: newVal } : p))
  }

  async function toggleStarting(player) {
    const newVal = !player.is_starting

    if (newVal === true) {
      // Marking as starting — find lowest vacant position slot
      const occupiedSlots = oppPlayers
        .filter(p => p.is_starting && p.id !== player.id)
        .map(p => p.position_slot)
        .filter(Boolean)
      const vacantSlot = [1,2,3,4,5,6,7,8,9,10,11,12,13,14,15]
        .find(n => !occupiedSlots.includes(n))
      await supabase.from('opposition_players')
        .update({ is_starting: true, position_slot: vacantSlot || null })
        .eq('id', player.id)
      setOppPlayers(prev => prev.map(p =>
        p.id === player.id ? { ...p, is_starting: true, position_slot: vacantSlot || null } : p
      ))
    } else {
      // Marking as not starting — clear their position slot
      await supabase.from('opposition_players')
        .update({ is_starting: false, position_slot: null })
        .eq('id', player.id)
      setOppPlayers(prev => prev.map(p =>
        p.id === player.id ? { ...p, is_starting: false, position_slot: null } : p
      ))
    }
  }

async function swapPositionSlots(playerA, playerB) {
    const slotA = playerA.position_slot
    const slotB = playerB.position_slot
    await Promise.all([
      supabase.from('opposition_players').update({ position_slot: slotB }).eq('id', playerA.id),
      supabase.from('opposition_players').update({ position_slot: slotA }).eq('id', playerB.id),
    ])
    setOppPlayers(prev => prev.map(p => {
      if (p.id === playerA.id) return { ...p, position_slot: slotB }
      if (p.id === playerB.id) return { ...p, position_slot: slotA }
      return p
    }))
    setSwapFirst(null)
  }

  async function swapNumbers(numA, numB) {
    const playerA = oppPlayers.find(p => p.jersey_number === numA)
    const playerB = oppPlayers.find(p => p.jersey_number === numB)
    if (!playerA || !playerB) return
    await Promise.all([
      supabase.from('opposition_players').update({ jersey_number: numB }).eq('id', playerA.id),
      supabase.from('opposition_players').update({ jersey_number: numA }).eq('id', playerB.id),
    ])
    setOppPlayers(prev => prev.map(p => {
      if (p.id === playerA.id) return { ...p, jersey_number: numB }
      if (p.id === playerB.id) return { ...p, jersey_number: numA }
      return p
    }))
  }

  // Get opp events with jersey number attribution
  const oppEvents = events.filter(e =>
    ['opp_goal', 'opp_point', 'opp_twopoint', 'opp_card'].includes(e.event_type)
  )

  function getPlayerName(jerseyNum) {
    if (!jerseyNum) return 'Unknown'
    const p = oppPlayers.find(p => p.jersey_number === jerseyNum)
    return p?.full_name || '#' + jerseyNum
  }

  const starters = oppPlayers.filter(p => p.is_starting).sort((a, b) => a.jersey_number - b.jersey_number)
  const nonStarters = oppPlayers.filter(p => !p.is_starting).sort((a, b) => a.jersey_number - b.jersey_number)

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#999' }}>Loading...</div>

  return (
    <div style={{ padding: '10px 14px' }}>
      <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
        {['lineup', 'events', 'names'].map(t => (
          <button key={t} onClick={() => setSubTab(t)} style={{
            flex: 1, padding: '8px 4px', background: 'none', border: 'none',
            borderBottom: subTab === t ? '2px solid ' + BLUE : '2px solid transparent',
            color: subTab === t ? BLUE : '#888',
            fontWeight: subTab === t ? 700 : 400,
            fontSize: 12, cursor: 'pointer',
          }}>
            {t === 'lineup' ? '📋 Lineup' : t === 'events' ? '⚡ Events' : '✏️ Names'}
          </button>
        ))}
      </div>

    {subTab === 'lineup' && (
        <div>
          {historyLoaded && (
            <div style={{ background: '#e8f5e9', border: '1px solid #81c784', borderRadius: 8, padding: '8px 12px', marginBottom: 10, fontSize: 12, color: '#2e7d32' }}>
              ✅ Names pre-filled from your last match against {fixture.opponent}
            </div>
          )}
        <div style={{ fontSize: 11, color: '#888', marginBottom: 10 }}>
            Tap ✓/✗ to mark starting status. Numbers 1–15 default to starting.
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Starting ({starters.length})
            </div>
            {swapFirst ? (
              <button onClick={() => setSwapFirst(null)} style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6, border: '1px solid #A32D2D', background: '#FCEBEB', cursor: 'pointer', color: '#A32D2D' }}>
                ✕ Cancel Swap
              </button>
            ) : (
              <button onClick={() => setSwapFirst('pending')} style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6, border: '1px solid #ccc', background: 'white', cursor: 'pointer', color: '#533AB7' }}>
                ⇄ Swap Position
              </button>
            )}
          </div>
          {swapFirst && swapFirst !== 'pending' && (
            <div style={{ background: '#FFF9E6', border: '1px solid #FFD700', borderRadius: 8, padding: '6px 10px', marginBottom: 6, fontSize: 12, color: '#854F0B' }}>
              Swapping <strong>#{swapFirst.jersey_number} {swapFirst.full_name || 'Unknown'}</strong> — tap another starter to swap positions
            </div>
          )}
          {starters.map(p => {
            const isSwapSelected = swapFirst && swapFirst !== 'pending' && swapFirst.id === p.id
            return (
              <div key={p.id}
                onClick={() => {
                  if (!swapFirst) return
                  if (swapFirst === 'pending') { setSwapFirst(p); return }
                  if (swapFirst.id === p.id) { setSwapFirst(null); return }
                  swapPositionSlots(swapFirst, p)
                }}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '0.5px solid #f0f0f0', cursor: swapFirst ? 'pointer' : 'default', background: isSwapSelected ? '#FFF9E6' : 'white' }}>
                <div style={{ width: 32, height: 32, borderRadius: '50%', background: isSwapSelected ? '#FFD700' : BLUE, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: isSwapSelected ? '#333' : 'white', flexShrink: 0 }}>
                  {p.jersey_number}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, color: p.full_name ? '#333' : '#bbb' }}>
                    {p.full_name || 'Unknown'}
                  </div>
                  <div style={{ fontSize: 10, color: '#aaa' }}>
                    {DEFAULT_POSITIONS.find(pos => pos.num === p.position_slot)?.label || ''}
                  </div>
                </div>
                {!swapFirst && (
                  <>
                    <button onClick={e => { e.stopPropagation(); toggleStandout(p) }} style={{ fontSize: 18, background: 'none', border: 'none', cursor: 'pointer', opacity: p.standout ? 1 : 0.3 }}>⭐</button>
                    <button onClick={e => { e.stopPropagation(); toggleStarting(p) }} style={{ fontSize: 18, background: 'none', border: 'none', cursor: 'pointer' }}>✓</button>
                  </>
                )}
              </div>
            )
          })}

          {nonStarters.length > 0 && (
            <>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#555', margin: '12px 0 6px', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Substitutes ({nonStarters.length})
              </div>
              {nonStarters.map(p => (
                <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '0.5px solid #f0f0f0', opacity: 0.6 }}>
                  <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#888', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: 'white', flexShrink: 0 }}>
                    {p.jersey_number}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, color: p.full_name ? '#333' : '#bbb' }}>
                      {p.full_name || 'Unknown'}
                    </div>
                  </div>
                  <button onClick={() => toggleStandout(p)} style={{ fontSize: 18, background: 'none', border: 'none', cursor: 'pointer', opacity: p.standout ? 1 : 0.3 }}>⭐</button>
                  <button onClick={() => toggleStarting(p)} style={{ fontSize: 18, background: 'none', border: 'none', cursor: 'pointer' }}>✗</button>
                </div>
              ))}
            </>
          )}
        </div>
      )}

      {subTab === 'events' && (
        <div>
          {oppEvents.length === 0 ? (
            <div style={{ padding: '24px 0', textAlign: 'center', color: '#bbb', fontSize: 13 }}>
              No opposition events logged yet
            </div>
          ) : oppEvents.map(e => {
            const playerName = e.opp_jersey_number ? getPlayerName(e.opp_jersey_number) : 'Unknown'
            const label = e.event_type === 'opp_goal' ? '⚽ Goal' : e.event_type === 'opp_point' ? '🎯 Point' : e.event_type === 'opp_twopoint' ? '🎯 2-Pointer' : '🟨 Card'
            return (
              <div key={e.id} style={{ display: 'flex', gap: 10, padding: '8px 0', borderBottom: '0.5px solid #f5f5f5', alignItems: 'center' }}>
                <span style={{ color: '#aaa', minWidth: 28, fontSize: 12 }}>{e.minute}'</span>
                <span style={{ fontSize: 13, flex: 1, color: '#333' }}>{label}</span>
                <span style={{ fontSize: 13, color: e.opp_jersey_number ? RED : '#bbb', fontWeight: e.opp_jersey_number ? 600 : 400 }}>
                  {playerName}
                </span>
              </div>
            )
          })}
        </div>
      )}

      {subTab === 'names' && (
        <div>
          <div style={{ fontSize: 11, color: '#888', marginBottom: 12 }}>
            Enter player names from the team sheet. Names link automatically to logged events.
          </div>
          {oppPlayers.sort((a, b) => a.jersey_number - b.jersey_number).map(p => (
            <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '0.5px solid #f0f0f0' }}>
              <div style={{ width: 32, height: 32, borderRadius: '50%', background: p.is_starting ? BLUE : '#888', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: 'white', flexShrink: 0 }}>
                {p.jersey_number}
              </div>
              {editingId === p.id ? (
                <div style={{ flex: 1, display: 'flex', gap: 6 }}>
                  <input
                    autoFocus
                    value={editName}
                    onChange={e => setEditName(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') saveName(p); if (e.key === 'Escape') { setEditingId(null); setEditName('') } }}
                    placeholder="Player name"
                    style={{ flex: 1, padding: '6px 10px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, outline: 'none' }}
                  />
                  <button onClick={() => saveName(p)} disabled={saving} style={{ padding: '6px 12px', background: BLUE, color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>
                    {saving ? '...' : 'Save'}
                  </button>
                  <button onClick={() => { setEditingId(null); setEditName('') }} style={{ padding: '6px 10px', background: '#f0f0f0', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>
                    ✕
                  </button>
                </div>
              ) : (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                  onClick={() => { setEditingId(p.id); setEditName(p.full_name || '') }}>
                  <span style={{ fontSize: 14, color: p.full_name ? '#333' : '#bbb' }}>
                    {p.full_name || 'Tap to add name'}
                  </span>
                  <span style={{ fontSize: 12, color: '#bbb' }}>✏️</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}